import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  LiveRatesTable,
  LiveChart,
  DeviationBadge,
  KsaLinkMatrix,
  FaqAccordion,
  buildKsaSchema,
  arDateParts,
  goldRatesQueryOptions,
} from "@/components/sa-ar/shell";
import { computeSaPrices, formatSar, SA_KARATS, type SaKarat } from "@/lib/sa-gold/data";
import { useGoldRates } from "@/lib/nonblocking-rates";

const SLUG = "/sa/ar/used-gold-rates";
const CANONICAL = `https://drhint.com${SLUG}`;

const FAQS = [
  { q: "كيف يمكنني معرفة سعر بيع الذهب المستعمل اليوم في السعودية؟", a: "احسب سعر الذهب المستعمل بخصم قيمة المصنعية والوزن المفقود وهامش التاجر (10–15%) من سعر الجرام الخام لعيار قطعتك السائد اليوم في السوق السعودي. استخدم الحاسبة أعلاه للحصول على تقدير فوري." },
  { q: "ما هو الفرق بين سعر بيع الذهب المستعمل وسعر شراء الذهب الجديد؟", a: "سعر بيع الذهب الجديد يشمل هامش الصاغة وأجرة الصياغة وربح المحل، بينما سعر شراء الذهب المستعمل من العميل يُخصم منه هامش التاجر (3–10 ريال للجرام عادة) وأجرة إعادة الصهر إذا كانت القطعة مكسورة." },
  { q: "كم يخصم تاجر الذهب من سعر الجرام عند شراء الذهب المستعمل؟", a: "يتراوح الخصم عادة من 3 ريال إلى 10 ريال للجرام في محلات الذهب المرخصة، ويعتمد على نوع القطعة وحالتها ووجود دمغة العيار." },
];

export const Route = createFileRoute("/sa/ar/used-gold-rates")({
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(goldRatesQueryOptions);
    return null;
  },
  head: () => {
    const title = `سعر بيع الذهب المستعمل اليوم في السعودية`;
    const description = `أسعار بيع وشراء الذهب المستعمل اليوم في السوق السعودي مع حاسبة تلقائية لخصم المصنعية والهامش، بالريال السعودي — عيار 24، 22، 21، 18.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "index,follow,max-snippet:-1,max-image-preview:large" },
        { httpEquiv: "content-language", content: "ar-SA" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: CANONICAL },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "ar_SA" },
      ],
      links: [
        { rel: "canonical", href: CANONICAL },
        { rel: "alternate", hreflang: "ar-SA", href: CANONICAL },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(buildKsaSchema({ slug: SLUG, name: title, description, faqs: FAQS })),
        },
      ],
    };
  },
  component: UsedGoldPage,
});

function UsedGoldPage() {
  const dp = arDateParts();
  return (
    <main dir="rtl" className="min-h-screen bg-background text-foreground">
      <header className="mx-auto max-w-6xl px-4 pt-8">
        <p className="text-xs font-semibold uppercase tracking-widest text-amber-500">KSA · سوق الذهب المستعمل</p>
        <h1 className="mt-2 text-2xl md:text-4xl font-black leading-tight">
          أسعار بيع وشراء الذهب المستعمل اليوم في السعودية — {dp.day} {dp.dayNum} {dp.month} {dp.year}
        </h1>
        <p className="mt-3 text-sm md:text-base text-muted-foreground max-w-3xl">
          دليل شامل لبيع ذهبك القديم في محلات الصاغة السعودية. يشمل السعر الفوري لكل عيار، الحاسبة التلقائية لخصم المصنعية، وشرح الفرق بين سعر الشراء وسعر البيع في السوق المحلي.
        </p>
        <div className="mt-3"><DeviationBadge /></div>
      </header>
      <LiveChart />
      <LiveRatesTable />
      <ScrapMatrix />
      <ScrapExplainer />
      <FaqAccordion items={FAQS} />
      <KsaLinkMatrix current={SLUG} />
    </main>
  );
}

function ScrapMatrix() {
  const { data } = useGoldRates();
  const prices = useMemo(() => computeSaPrices({ spotUsdPerOz: data.spotUsdPerOz }), [data.spotUsdPerOz]);
  const [markdownPct, setMarkdownPct] = useState(12); // 10–15% standard scrap deduction
  return (
    <section className="mx-auto max-w-6xl px-4 py-8">
      <h2 className="text-xl md:text-2xl font-bold mb-2">مصفوفة أسعار بيع الذهب المستعمل — بعد خصم المصنعية</h2>
      <p className="text-sm text-muted-foreground mb-4">
        هذه المصفوفة تُظهر سعر شراء التاجر لذهبك المستعمل بعد خصم نسبة الهامش التشغيلي والمصنعية (المعيار: 10% إلى 15% في السوق السعودي).
      </p>
      <label className="block text-sm mb-4">
        نسبة الخصم القياسية: <span className="font-bold text-amber-600 dark:text-amber-300">{markdownPct}%</span>
        <input type="range" min={5} max={20} value={markdownPct} onChange={(e) => setMarkdownPct(Number(e.target.value))} className="w-full accent-amber-500 mt-2" />
      </label>
      <div className="overflow-hidden rounded-xl border border-amber-500/30 bg-card">
        <table className="w-full text-sm tabular-nums">
          <thead className="bg-amber-500/10 text-right">
            <tr>
              <th className="px-3 py-2">العيار</th>
              <th className="px-3 py-2 text-left">سعر السوق / جرام</th>
              <th className="px-3 py-2 text-left">سعر شراء التاجر</th>
              <th className="px-3 py-2 text-left">الفرق (خسارتك)</th>
            </tr>
          </thead>
          <tbody>
            {SA_KARATS.map((k: SaKarat) => {
              const raw = prices[k];
              const buy = raw * (1 - markdownPct / 100);
              return (
                <tr key={k} className="border-t border-amber-500/10">
                  <th className="px-3 py-2 text-right font-medium">عيار {k}</th>
                  <td className="px-3 py-2 text-left">{formatSar(raw)}</td>
                  <td className="px-3 py-2 text-left font-semibold text-amber-700 dark:text-amber-300">{formatSar(buy)}</td>
                  <td className="px-3 py-2 text-left text-red-500">−{formatSar(raw - buy)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ScrapExplainer() {
  return (
    <section className="mx-auto max-w-6xl px-4 py-4">
      <div className="rounded-2xl border border-border bg-muted/30 p-5">
        <h2 className="text-lg md:text-xl font-bold mb-3">الفرق بين "سعر الشراء" و"سعر البيع" في محلات الصاغة السعودية</h2>
        <div className="grid gap-4 sm:grid-cols-2 text-sm leading-relaxed">
          <div>
            <div className="font-semibold text-emerald-600 dark:text-emerald-400 mb-1">سعر الشراء (يشتري منك التاجر)</div>
            <p className="text-muted-foreground">هذا السعر يُخصم منه: هامش التاجر (3–10 ريال/جرام)، أجرة إعادة الصهر، تكلفة الفحص، وأي وزن مفقود بسبب اللحامات أو الأحجار المرصعة.</p>
          </div>
          <div>
            <div className="font-semibold text-red-500 mb-1">سعر البيع (تشتري أنت من التاجر)</div>
            <p className="text-muted-foreground">هذا السعر يُضاف عليه: أجرة الصياغة (5–40 ريال/جرام حسب التصميم)، ربح المحل، وضريبة القيمة المضافة 15% على المشغولات (السبائك الاستثمارية معفاة).</p>
          </div>
        </div>
      </div>
    </section>
  );
}